import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Popconfirm, Card, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CommentOutlined } from '@ant-design/icons';
import {
  fetchComplaintItems,
  createComplaintItem,
  updateComplaintItem,
  deleteComplaintItem,
  type ComplaintItemDto
} from '../services/serviceOrderService';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

const ComplaintItems: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [items, setItems] = useState<ComplaintItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ComplaintItemDto | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchComplaintItems();
      setItems(data);
    } catch (error) {
      message.error('Şikayet kalemleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (item: ComplaintItemDto) => {
    setEditingItem(item);
    form.setFieldsValue({
      name: item.name,
      description: item.description,
      isActive: item.isActive
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        await updateComplaintItem(editingItem.id, {
          name: values.name,
          description: values.description,
          isActive: values.isActive !== undefined ? values.isActive : true
        });
        message.success('Şikayet kalemi güncellendi');
      } else {
        await createComplaintItem({
          name: values.name,
          description: values.description
        });
        message.success('Şikayet kalemi eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      loadItems();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteComplaintItem(id);
      message.success('Şikayet kalemi silindi');
      loadItems();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Şikayet kalemi silinirken hata oluştu');
      }
    }
  };

  const columns = [
    {
      title: 'Şikayet Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ComplaintItemDto) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu şikayet kalemini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>
          <CommentOutlined /> Şikayet Kalemleri
        </h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Şikayet Kalemi Ekle
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          scroll={{ x: 400 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} şikayet kalemi`,
          }}
        />
      </Card>

      <Modal
        title={editingItem ? 'Şikayet Kalemini Düzenle' : 'Yeni Şikayet Kalemi Ekle'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Şikayet Adı"
            rules={[{ required: true, message: 'Lütfen şikayet adı girin' }]}
          >
            <Input placeholder="Örn: Motor arızası, Fren sesi, Klima çalışmıyor" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} placeholder="Şikayet açıklaması (opsiyonel)" />
          </Form.Item>
          {editingItem && (
            <Form.Item name="isActive" label="Aktif" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ComplaintItems;
